import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/dashboard/admin';
    if (user.role === 'landlord') return '/dashboard/landlord';
    return '/dashboard/tenant';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-stone-200/50 shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline" data-testid="logo-link">
            <span className="font-outfit font-black text-emerald-900 text-2xl tracking-tighter">
              4L<span className="text-emerald-500">.</span>
            </span>
            <span className="font-outfit font-semibold text-stone-700 text-sm hidden sm:inline">4 Longevity</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <a href="/#services" className="text-sm font-medium text-stone-600 hover:text-emerald-900 transition-colors">Services</a>
            <a href="/#calculators" className="text-sm font-medium text-stone-600 hover:text-emerald-900 transition-colors">Calculators</a>
            <a href="/#faq" className="text-sm font-medium text-stone-600 hover:text-emerald-900 transition-colors">FAQ</a>
            <a href="/#contact" className="text-sm font-medium text-stone-600 hover:text-emerald-900 transition-colors">Contact</a>
          </nav>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:1-888-419-9767" className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-emerald-900 transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span>1-888-419-9767</span>
            </a>
            {user && user.id ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-sm font-medium text-stone-700" data-testid="user-menu-trigger">
                    {user.name || user.email} <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(getDashboardPath())} data-testid="dashboard-link">
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" className="text-sm text-stone-700" onClick={() => navigate('/login')} data-testid="login-nav-button">
                  Log In
                </Button>
                <Button className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-6 text-sm" onClick={() => navigate('/quote')} data-testid="get-quote-nav-button">
                  Get a Quote
                </Button>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden p-2 text-stone-700" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-stone-200" data-testid="mobile-menu">
            <nav className="flex flex-col gap-3">
              <a href="/#services" className="text-sm font-medium text-stone-600 py-2" onClick={() => setMobileOpen(false)}>Services</a>
              <a href="/#calculators" className="text-sm font-medium text-stone-600 py-2" onClick={() => setMobileOpen(false)}>Calculators</a>
              <a href="/#faq" className="text-sm font-medium text-stone-600 py-2" onClick={() => setMobileOpen(false)}>FAQ</a>
              <a href="/#contact" className="text-sm font-medium text-stone-600 py-2" onClick={() => setMobileOpen(false)}>Contact</a>
              <a href="tel:1-888-419-9767" className="flex items-center gap-2 text-sm text-stone-600 py-2">
                <Phone className="w-4 h-4" /> 1-888-419-9767
              </a>
              {user && user.id ? (
                <>
                  <Button variant="outline" onClick={() => { navigate(getDashboardPath()); setMobileOpen(false); }}>Dashboard</Button>
                  <Button variant="ghost" onClick={() => { handleLogout(); setMobileOpen(false); }}>Log Out</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { navigate('/login'); setMobileOpen(false); }}>Log In</Button>
                  <Button className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" onClick={() => { navigate('/quote'); setMobileOpen(false); }}>Get a Quote</Button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
